import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  getUserDetails,
  login,
  logout,
  resendLoginCode,
  verifyLoginCode,
} from "../../services/authService";
import { recentSearchCategoryLabelHe } from "../../utils/agentRecentSearchPayload";
import { formatRecentSearchDates } from "../../utils/formatRecentSearchDates";
import { canAccessTripMasterOps } from "./tripMasterOpsAccess";
import {
  fetchTripMasterOpsOrderDetail,
  fetchTripMasterOpsOrders,
  fetchTripMasterOpsSearches,
} from "./tripMasterOpsApi";
import "./TripMasterOpsApp.scss";

function loginFailureMessage(result, fallback) {
  const raw = String(result?.message || "").trim();
  if (!raw || /failed to login|login failed|unexpected response|verification failed|invalid verification/i.test(raw)) {
    return fallback;
  }
  return raw;
}

function formatWhen(iso) {
  if (!iso) return "—";
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : String(iso);
}

function OpsLogin({ onAuthenticated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [verification, setVerification] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function acceptUser() {
    const user = getUserDetails();
    if (!canAccessTripMasterOps(user)) {
      await logout();
      setError("החשבון הזה אינו חשבון אופרציה.");
      setVerification(null);
      return;
    }
    onAuthenticated(user);
  }

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (verification) {
        const result = await verifyLoginCode(verification.verification_session, code.trim());
        if (result?.status !== "ok") {
          setError(loginFailureMessage(result, "הקוד לא התקבל."));
          return;
        }
        await acceptUser();
        return;
      }
      const result = await login({ email: email.trim(), password, externalID: "" });
      if (result?.requires_2fa || result?.status === "requires_2fa") {
        setVerification(result);
        return;
      }
      if (result?.status !== "ok") {
        setError(loginFailureMessage(result, "ההתחברות נכשלה. בדקו אימייל וסיסמה."));
        return;
      }
      await acceptUser();
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="tm-ops tm-ops--login" dir="rtl">
      <form className="tm-ops__card" onSubmit={onSubmit}>
        <p className="tm-ops__kicker">TripMaster</p>
        <h1 className="tm-ops__title">כניסת אופרציה</h1>
        {verification ? (
          <>
            <p className="tm-ops__hint">
              נשלח קוד אימות
              {verification.masked_email ? ` אל ${verification.masked_email}` : ""}.
            </p>
            <label className="tm-ops__field">
              קוד
              <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />
            </label>
            <button
              type="button"
              className="tm-ops__text-btn"
              disabled={busy}
              onClick={() => resendLoginCode(verification.verification_session)}
            >
              שליחה מחדש
            </button>
          </>
        ) : (
          <>
            <label className="tm-ops__field">
              אימייל
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label className="tm-ops__field">
              סיסמה
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
          </>
        )}
        {error ? <p className="tm-ops__error">{error}</p> : null}
        <button className="tm-ops__submit" type="submit" disabled={busy}>
          {busy ? "מתחברים…" : verification ? "אימות" : "כניסה"}
        </button>
      </form>
    </main>
  );
}

function OpsSearches() {
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchTripMasterOpsSearches();
        if (cancelled) return;
        setItems(Array.isArray(res?.items) ? res.items : []);
        setState("ready");
      } catch {
        if (!cancelled) setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "loading") return <p className="tm-ops__status">טוען חיפושים…</p>;
  if (state === "error") return <p className="tm-ops__status">טעינת החיפושים נכשלה.</p>;
  if (!items.length) return <p className="tm-ops__status">אין חיפושים אחרונים בטריפמאסטר.</p>;

  return (
    <div className="tm-ops__table-wrap">
      <table className="tm-ops__table">
        <thead>
          <tr>
            <th>מתי</th>
            <th>יעד</th>
            <th>תאריכים</th>
            <th>סוג</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, index) => (
            <tr key={row.id || index}>
              <td>{formatWhen(row.performed_at || row.created_at || row.createdAt)}</td>
              <td>
                {row.destination_label || row.destination_code || "—"}
                {row.destination_code && row.destination_label ? (
                  <span className="tm-ops__muted"> {row.destination_code}</span>
                ) : null}
              </td>
              <td>{formatRecentSearchDates(row)}</td>
              <td>{recentSearchCategoryLabelHe(row)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="tm-ops__detail-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function OpsOrderDetail({ orderId }) {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchTripMasterOpsOrderDetail(orderId);
        if (cancelled) return;
        if (res?.ok === false || !res?.item) {
          setState(res?.error === "not_found" ? "missing" : "error");
          return;
        }
        setOrder(res.item);
        setState("ready");
      } catch (error) {
        if (cancelled) return;
        const status = error?.response?.status || error?.status;
        setState(status === 404 ? "missing" : status === 403 ? "forbidden" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  if (state === "loading") return <p className="tm-ops__status">טוען פרטי הזמנה…</p>;
  if (state === "forbidden") return <p className="tm-ops__status">אין הרשאה להזמנה זו.</p>;
  if (state === "missing") return <p className="tm-ops__status">ההזמנה לא נמצאה בטריפמאסטר.</p>;
  if (state === "error" || !order) return <p className="tm-ops__status">טעינת פרטי ההזמנה נכשלה.</p>;

  const hotel = order.hotel || {};
  const contact = order.contact || {};
  const passengers = Array.isArray(order.passengers) ? order.passengers : [];
  const flights = Array.isArray(order.flights) ? order.flights : [];

  return (
    <div className="tm-ops__detail">
      <button type="button" className="tm-ops__text-btn" onClick={() => navigate("/ops/orders")}>
        חזרה להזמנות
      </button>
      <header className="tm-ops__detail-hero">
        <div>
          <p className="tm-ops__kicker">הזמנת טריפמאסטר</p>
          <h2 className="tm-ops__detail-title">{order.title || "הזמנה"}</h2>
          <p className="tm-ops__muted">{order.dates || "—"}</p>
        </div>
        <div className="tm-ops__detail-badges">
          <span>{order.status_label || "—"}</span>
          <span>{order.amount_label || "—"}</span>
        </div>
      </header>

      <section className="tm-ops__detail-card">
        <h3>פרטי הזמנה</h3>
        <dl className="tm-ops__detail-grid">
          <DetailRow label="מספר הזמנה" value={order.reference} />
          <DetailRow label="נוצרה" value={formatWhen(order.created_at)} />
          <DetailRow label="סוג" value={order.deal_type} />
          <DetailRow label="PNR ספק" value={order.supplier_pnr} />
          <DetailRow label="סטטוס טיוטה" value={order.draft_status} />
        </dl>
      </section>

      <section className="tm-ops__detail-card">
        <h3>מלון</h3>
        <dl className="tm-ops__detail-grid">
          <DetailRow label="שם" value={hotel.name} />
          <DetailRow label="צ׳ק-אין" value={hotel.check_in} />
          <DetailRow label="צ׳ק-אאוט" value={hotel.check_out} />
          <DetailRow label="בסיס אירוח" value={hotel.board} />
        </dl>
      </section>

      <section className="tm-ops__detail-card">
        <h3>איש קשר</h3>
        <dl className="tm-ops__detail-grid">
          <DetailRow label="שם" value={contact.full_name} />
          <DetailRow label="אימייל" value={contact.email} />
          <DetailRow label="טלפון" value={contact.phone} />
        </dl>
      </section>

      <section className="tm-ops__detail-card">
        <h3>נוסעים</h3>
        {passengers.length ? (
          <ul className="tm-ops__people">
            {passengers.map((person, index) => (
              <li key={`${person.full_name || "pax"}-${index}`}>
                <strong>{person.full_name || "נוסע"}</strong>
                <span>{person.pax_type || "—"}</span>
                <span>{person.email || "—"}</span>
                <span>{person.phone || "—"}</span>
                <span>{person.birth_date || "—"}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="tm-ops__muted">אין פרטי נוסעים.</p>
        )}
      </section>

      {flights.length ? (
        <section className="tm-ops__detail-card">
          <h3>טיסות</h3>
          <ul className="tm-ops__people">
            {flights.map((flight, index) => (
              <li key={`${flight.flight_number || "flight"}-${index}`}>
                <strong>
                  {(flight.from || "—") + " → " + (flight.to || "—")}
                </strong>
                <span>{flight.airline || "—"}</span>
                <span>{flight.flight_number || "—"}</span>
                <span>{flight.departure || "—"}</span>
                <span>{flight.arrival || "—"}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function OpsOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchTripMasterOpsOrders();
        if (cancelled) return;
        if (res?.ok === false) {
          setState(res?.error === "forbidden" ? "forbidden" : "error");
          return;
        }
        setOrders(Array.isArray(res?.items) ? res.items : []);
        setState("ready");
      } catch (error) {
        if (cancelled) return;
        const status = error?.response?.status || error?.status;
        setState(status === 403 ? "forbidden" : "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "loading") return <p className="tm-ops__status">טוען הזמנות…</p>;
  if (state === "forbidden") return <p className="tm-ops__status">אין הרשאה לרשימת ההזמנות.</p>;
  if (state === "error") return <p className="tm-ops__status">טעינת ההזמנות נכשלה.</p>;
  if (!orders.length) return <p className="tm-ops__status">אין הזמנות אחרונות בטריפמאסטר.</p>;

  return (
    <div className="tm-ops__table-wrap">
      <table className="tm-ops__table">
        <thead>
          <tr>
            <th>מתי</th>
            <th>הזמנה</th>
            <th>תאריכים</th>
            <th>סטטוס</th>
            <th>סכום</th>
            <th>מספר</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order, index) => (
            <tr
              key={order?.id || index}
              className="tm-ops__table-row--clickable"
              onClick={() => {
                if (order?.id) navigate(`/ops/orders/${order.id}`);
              }}
              onKeyDown={(event) => {
                if ((event.key === "Enter" || event.key === " ") && order?.id) {
                  event.preventDefault();
                  navigate(`/ops/orders/${order.id}`);
                }
              }}
              role="link"
              tabIndex={0}
            >
              <td>{formatWhen(order?.created_at)}</td>
              <td>{order?.title || "הזמנה"}</td>
              <td>{order?.dates || "—"}</td>
              <td>{order?.status_label || "—"}</td>
              <td>{order?.amount_label || "—"}</td>
              <td>{order?.reference || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function resolveOpsScreen(pathname) {
  const path = String(pathname || "").replace(/\/+$/, "") || "/ops";
  if (path === "/ops/orders") return { kind: "orders" };
  const detailMatch = path.match(/^\/ops\/orders\/([^/]+)$/);
  if (detailMatch) return { kind: "order_detail", orderId: decodeURIComponent(detailMatch[1]) };
  return { kind: "searches" };
}

export default function TripMasterOpsApp() {
  const location = useLocation();
  const [user, setUser] = useState(() => {
    const current = getUserDetails();
    return current && current.sessionID ? current : null;
  });

  useEffect(() => {
    if (user && !canAccessTripMasterOps(user)) {
      logout().finally(() => setUser(null));
    }
  }, [user]);

  if (!user) {
    return <OpsLogin onAuthenticated={setUser} />;
  }

  const screen = resolveOpsScreen(location.pathname);
  const ordersNavActive = screen.kind === "orders" || screen.kind === "order_detail";

  return (
    <div className="tm-ops" dir="rtl">
      <header className="tm-ops__bar">
        <div>
          <p className="tm-ops__kicker">TripMaster</p>
          <h1 className="tm-ops__title">אופרציה</h1>
        </div>
        <nav className="tm-ops__nav" aria-label="מסכי אופרציה">
          <NavLink to="/ops" end className={({ isActive }) => (isActive && !ordersNavActive ? "is-active" : undefined)}>
            חיפושים אחרונים
          </NavLink>
          <NavLink to="/ops/orders" className={() => (ordersNavActive ? "is-active" : undefined)}>
            הזמנות אחרונות
          </NavLink>
          <button
            type="button"
            className="tm-ops__text-btn"
            onClick={() => {
              logout().finally(() => setUser(null));
            }}
          >
            יציאה
          </button>
        </nav>
      </header>
      <main className="tm-ops__main">
        {screen.kind === "order_detail" ? (
          <OpsOrderDetail orderId={screen.orderId} />
        ) : screen.kind === "orders" ? (
          <OpsOrders />
        ) : (
          <OpsSearches />
        )}
      </main>
    </div>
  );
}
